// DESCRIPTION: Sticky sidebar that summarises the in-progress booking (event, date,
// DESCRIPTION: time, location, attendee, total). Fills in live as the wizard advances.

import React from 'react';
import { __ } from '@wordpress/i18n';
import { applyFilters } from '@wordpress/hooks';
import { formatTime } from '../calendar/utils';
import { eventTypeLabel, formatDateMedium, formatMoney } from './helpers';

/**
 * BookingSummary — live order summary shown beside the booking wizard.
 *
 * @param {Object}  props
 * @param {Object}  props.event        The event object from the REST API.
 * @param {boolean} props.slotBased    Whether the event uses date/slot selection.
 * @param {string}  props.selectedDate Selected ISO date (slot-based only).
 * @param {Object}  props.selectedSlot Selected slot object { start, end } (slot-based only).
 * @param {string}  props.attendeeName Attendee display name, shown once entered.
 *
 * @return {JSX.Element} The summary card.
 */
const BookingSummary = ( {
	event,
	slotBased,
	selectedDate,
	selectedSlot,
	attendeeName,
} ) => {
	const price = event ? event.price || 0 : 0;
	const symbol = event ? event.currency_symbol || event.currency || '' : '';
	const duration = event && event.duration ? event.duration : 0;

	const typeParts = [ eventTypeLabel( event && event.event_type ) ];
	if ( duration ) {
		typeParts.push( `${ duration } ${ __( 'min', 'nemtly-booking' ) }` );
	}
	const typeLine = typeParts.join( ' · ' );

	const slotDate = selectedDate ? formatDateMedium( selectedDate ) : '';
	const dateText = slotBased ? slotDate : ( event && event.date ) || '';

	const slotRange = selectedSlot
		? `${ formatTime( selectedSlot.start ) } – ${ formatTime(
				selectedSlot.end
		  ) }`
		: '';
	const timeText = slotBased ? slotRange : ( event && event.time ) || '';

	const baseRows = [
		{
			key: 'date',
			icon: '📅',
			content: dateText ? (
				<span>{ dateText }</span>
			) : (
				<span className="nb-summary__placeholder">
					{ __( 'Date not yet chosen', 'nemtly-booking' ) }
				</span>
			),
		},
		{
			key: 'time',
			icon: '🕙',
			content: timeText ? (
				<span>{ timeText }</span>
			) : (
				<span className="nb-summary__placeholder">
					{ __( 'Time not yet chosen', 'nemtly-booking' ) }
				</span>
			),
		},
	];

	if ( event && event.location ) {
		baseRows.push( {
			key: 'location',
			icon: '📍',
			content: <span>{ event.location }</span>,
		} );
	}

	if ( attendeeName ) {
		baseRows.push( {
			key: 'attendee',
			icon: '👤',
			content: <span>{ attendeeName }</span>,
		} );
	}

	/**
	 * Filters the rows shown in the booking summary body. Each row is
	 * `{ key, icon, content }`; the price/total row is appended separately
	 * and is not part of this filter.
	 *
	 * @param {Array}  rows Row descriptors.
	 * @param {Object} ctx  Booking context: { event, slotBased, selectedDate, selectedSlot, attendeeName }.
	 */
	const rows = applyFilters(
		'nemtly_booking.booking.summary_rows',
		baseRows,
		{
			event,
			slotBased,
			selectedDate,
			selectedSlot,
			attendeeName,
		}
	);

	return (
		<div
			className="nb-summary"
			aria-label={ __( 'Booking summary', 'nemtly-booking' ) }
		>
			<div className="nb-summary__head">
				<div className="nb-summary__type">{ typeLine }</div>
				<div className="nb-summary__name">
					{ event ? event.title?.rendered || event.title || '' : '' }
				</div>
			</div>
			<div className="nb-summary__body">
				{ rows.map( ( row ) => (
					<div className="nb-summary__row" key={ row.key }>
						<span className="nb-summary__icon" aria-hidden="true">
							{ row.icon }
						</span>
						{ row.content }
					</div>
				) ) }
				{ price > 0 && (
					<>
						<div className="nb-summary__divider" />
						<div className="nb-summary__total">
							<span>{ __( 'Total', 'nemtly-booking' ) }</span>
							<span>{ formatMoney( symbol, price ) }</span>
						</div>
					</>
				) }
			</div>
		</div>
	);
};

export default BookingSummary;
